import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, DownloadSimple } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { VideoEmbed } from '../components/safety/VideoEmbed';
import { SafetyQuiz } from '../components/safety/SafetyQuiz';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderContent(content) {
  return content.split('\n').map((line, idx) => {
    if (line.trim().startsWith('- ')) {
      return (
        <li key={`${line}-${idx}`} className="ml-6 list-disc text-gray-700">
          {line.trim().substring(2)}
        </li>
      );
    }
    if (line.trim() === '') {
      return <br key={`br-${idx}`} />;
    }
    return (
      <p key={`${line.slice(0, 30)}-${idx}`} className="text-gray-700 mb-2">
        {line}
      </p>
    );
  });
}

function openPrintablePdf(module) {
  const sectionsHtml = module.content_sections
    .map(
      (s) => `<h2 style="color:#14532d;margin-top:24px;">${s.heading}</h2><div style="white-space:pre-wrap;line-height:1.6;">${s.content}</div>`
    )
    .join('');
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head><title>${module.title} - PestPro Hub</title>
    <style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 20px;color:#1f2937;}
    h1{color:#14532d;border-bottom:3px solid #16a34a;padding-bottom:12px;}
    .footer{margin-top:40px;padding-top:16px;border-top:1px solid #d1d5db;font-size:12px;color:#6b7280;}</style>
    </head><body>
    <h1>${module.title}</h1>
    <p><em>${module.description}</em></p>
    ${sectionsHtml}
    <div class="footer">PestPro Hub Safety Training Hub &mdash; Printed ${new Date().toLocaleDateString()}</div>
    <script>window.onload = function(){ window.print(); }</` + `script>
    </body></html>`);
  win.document.close();
}

export default function SafetyModuleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchModule = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/safety-modules/${id}`);
      setModule(data);
    } catch (error) {
      console.error('Error fetching module:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20 text-gray-500">Loading module...</div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="text-center py-20">
          <p className="text-gray-500 mb-4">Training module not found.</p>
          <Link to="/safety-training" className="text-accent hover:underline">Back to Safety Training</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link
          to="/safety-training"
          className="flex items-center gap-2 text-accent hover:underline mb-8 text-sm"
          data-testid="back-to-safety-link"
        >
          <ArrowLeft size={16} /> Back to Safety Training
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-3" data-testid="module-title">{module.title}</h1>
            <p className="text-base text-gray-600">{module.description}</p>
          </div>
          <button
            onClick={() => openPrintablePdf(module)}
            className="flex items-center gap-2 bg-primary text-text_light px-5 py-3 rounded-sm hover:bg-primary/90 transition-colors text-sm whitespace-nowrap"
            data-testid="download-pdf-btn"
          >
            <DownloadSimple size={18} /> Download PDF
          </button>
        </div>

        {module.video_url && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-primary mb-4">Training Video</h2>
            <VideoEmbed videoUrl={module.video_url} videoType={module.video_type} title={module.title} />
          </div>
        )}

        <div className="space-y-8 mb-12">
          {module.content_sections.map((section) => (
            <div key={section.heading} className="bg-white border border-borders rounded-sm p-8" data-testid="module-section">
              <h2 className="text-lg font-semibold text-primary mb-4">{section.heading}</h2>
              <div className="text-sm leading-relaxed">{renderContent(section.content)}</div>
            </div>
          ))}
        </div>

        <SafetyQuiz module={module} user={user} />
      </div>
    </div>
  );
}
